import React, { useEffect, useState, useCallback, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import InstallmentSchedule from "../components/installment-schedule/InstallmentSchedule";
import {
  fetchInstallmentScheduleAccountList,
  fetchInstallmentScheduleAccountDetails
} from "../actions/installmentScheduleActions";
import { fetchBillingActivity } from "../actions/billingActivityActions";
import { Translations } from "../components/translations";
import DocumentLink from "../components/account-summary-v2/DocumentLink";
import PlainErrorView from "../components/PlainErrorView";
import { AnalyticCategories, AnalyticActions } from "../constants/analytics";
import { logger } from "../logging";
import { DirectBillEmailLink } from "../components/email";
import {
  ContentHeader,
  SelectNonForm,
  FlexRow,
  Alert,
  LoadingIndicator
} from "@ufginsurance/ui-kit";
import SmartPayLink from "../components/SmartPayLink";
import { isPayGo, getPaymentType } from "../utils";
import HelpDeskMessage from "../help-desk/HelpDeskMessage";

const InstallmentScheduleContainer = ({
  installmentScheduleData,
  fetchInstallmentScheduleAccountList,
  fetchInstallmentScheduleAccountDetails,
  fetchBillingActivity,
  billingActivityData,
  match
}) => {
  const {
    installment_schedule: { title, info_message }
  } = Translations;

  const [installmentAccounts, setInstallmentAccounts] = useState(
    installmentScheduleData.accounts
  );
  const [installmentAccountDetails, setInstallmentAccountDetails] = useState(
    installmentScheduleData.details
  );

  const [activeAccount, setActiveAccount] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const fetchedAccountsRef = useRef(new Set());

  const fetchInstallmentScheduleAccountListCallback = useCallback(() => {
    return fetchInstallmentScheduleAccountList();
  }, [fetchInstallmentScheduleAccountList]);

  const fetchBillingActivityCallback = useCallback(() => {
    return fetchBillingActivity();
  }, [fetchBillingActivity]);

  const fetchInstallmentScheduleAccountDetailsCallback = useCallback(
    accountNumber => {
      return fetchInstallmentScheduleAccountDetails(accountNumber);
    },
    [fetchInstallmentScheduleAccountDetails]
  );

  const trackAnalytics = details => {
    logger.event({
      category: AnalyticCategories.account,
      action: AnalyticActions.account.fetchInstallmentSchedule,
      label: `Account: ${details}`
    });
  };

  const handleChange = ({ value }) => {
    setActiveAccount(value);
    trackAnalytics(value);
    const paymentType = getPaymentType(billingActivityData, value);
    setPaymentType(paymentType);
  };

  const renderAccountDropdown = accountNumbers => {
    if (accountNumbers) {
      const accountOptions = accountNumbers
        .map(num => ({
          value: num,
          label: num
        }))
        .sort((a, b) => b?.value - a?.value);
      return (
        <SelectNonForm
          id="accountNumber"
          name="accountNumber"
          label="Select Account"
          placeholder="Select Account"
          options={accountOptions}
          onChange={handleChange}
          value={activeAccount}
          isClearable={false}
          size="md"
        />
      );
    }
  };

  const renderAccount = () => {
    const { accountDetails } = installmentAccountDetails;

    const { is_current_message, changed_values_message } =
      Translations.installment_schedule;
    if (accountDetails && activeAccount && !isPayGo(paymentType)) {
      return (
        <div>
          <div className="installment-schedule__info-message">
            {is_current_message}
          </div>
          <InstallmentSchedule account={accountDetails} />
          {changed_values_message}
        </div>
      );
    }
  };

  const renderInstallmentSchedule = () => {
    const { accountNumbers } = installmentAccounts;
    const { isLoading, error } = installmentAccountDetails;

    const { print, no_accounts } = Translations.installment_schedule;

    return accountNumbers && accountNumbers.length > 0 ? (
      <div>
        <div className="installment-schedule__sub-header">
          <FlexRow align="justify">
            {renderAccountDropdown(accountNumbers)}

            {isLoading && <LoadingIndicator />}
            {activeAccount !== "" && !error && !isLoading && (
              <DocumentLink
                className="installment-schedule__print-button"
                color="primary"
                isPostRequest
                postPayload={installmentAccountDetails.accountDetails}
                pdfUri={`${window.env.REACT_APP_UFGAPI_BASEURL}/xapi/policyholder/billing/installment-schedules/pdf`}
                filename="Installment_Schedule_Statement.pdf"
                disabled={isPayGo(paymentType)}
              >
                <span>{print}</span>
              </DocumentLink>
            )}
          </FlexRow>
          {isPayGo(paymentType) && (
            <Alert dismissible={false} className="wc-smart-pay-alert">
              This Workers&apos; Compensation Policy is managed by SmartPay
              <SmartPayLink />
            </Alert>
          )}
        </div>
        {renderAccount()}
        <br />
      </div>
    ) : (
      !error && (
        <div className="no-accounts">
          {Translations.formatString(no_accounts, <DirectBillEmailLink />)}
        </div>
      )
    );
  };

  useEffect(() => {
    setInstallmentAccounts(installmentScheduleData.accounts);
  }, [installmentScheduleData.accounts]);

  useEffect(() => {
    setInstallmentAccountDetails(installmentScheduleData.details);
  }, [installmentScheduleData.details]);

  useEffect(() => {
    const { isLoading, error, accountNumbers } = installmentAccounts;
    if (isLoading || error || accountNumbers !== null) return;
    fetchInstallmentScheduleAccountListCallback();
    fetchBillingActivityCallback().then(() => {
      if (!!match?.params.accountNumber) {
        setActiveAccount(match.params.accountNumber);
      }
    });
  }, [
    match,
    fetchInstallmentScheduleAccountListCallback,
    fetchBillingActivityCallback,
    installmentAccounts
  ]);

  useEffect(() => {
    const { isLoading } = installmentAccountDetails;
    if (!activeAccount || isLoading) return;
    if (fetchedAccountsRef.current.has(activeAccount)) return;
    fetchedAccountsRef.current.clear();
    fetchedAccountsRef.current.add(activeAccount);
    fetchInstallmentScheduleAccountDetailsCallback(activeAccount);
  }, [
    activeAccount,
    fetchInstallmentScheduleAccountDetailsCallback,
    installmentAccountDetails
  ]);

  useEffect(() => {
    const { isLoading, error, accountNumbers } = installmentAccounts;
    if (isLoading || error || !accountNumbers) return;

    if (accountNumbers?.length === 1) {
      setActiveAccount(accountNumbers[0]);
      const paymentType = getPaymentType(
        billingActivityData,
        accountNumbers[0]
      );
      setPaymentType(paymentType);
    }
  }, [installmentAccounts, billingActivityData]);

  return (
    <div>
      <ContentHeader>{title}</ContentHeader>
      {installmentAccounts.isLoading && <LoadingIndicator />}
      {!installmentAccounts.isLoading && (
        <>
          <div className="installment-schedule__info-message">
            <i className="fa fa-info-circle" />
            <em>
              {" "}
              {Translations.formatString(info_message, <DirectBillEmailLink />)}
            </em>
          </div>
          {renderInstallmentSchedule()}
        </>
      )}
      {installmentAccounts.error && (
        <HelpDeskMessage display="anErrorOccurred" />
      )}
      {installmentAccountDetails.error && (
        <PlainErrorView
          hideDirectBillLink={false}
          error={installmentAccountDetails.error}
          errorLocationInTranslation={
            Translations.installment_schedule.error_messages
          }
          helpDeskErrorCodes={["900", "500", "502"]}
        />
      )}
    </div>
  );
};

InstallmentScheduleContainer.propTypes = {
  installmentScheduleData: PropTypes.object,
  fetchInstallmentScheduleAccountList: PropTypes.func.isRequired,
  fetchInstallmentScheduleAccountDetails: PropTypes.func.isRequired,
  fetchBillingActivity: PropTypes.func.isRequired,
  billingActivityData: PropTypes.object,
  match: PropTypes.any.isRequired
};

const mapStateToProps = state => ({
  installmentScheduleData: state.installmentScheduleData,
  billingActivityData: state.billingActivityData
});

const mapDispatchToProps = {
  fetchInstallmentScheduleAccountList,
  fetchInstallmentScheduleAccountDetails,
  fetchBillingActivity
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(InstallmentScheduleContainer);
